import { TrackedLink } from "@components/analytics/TrackedLink";
import Breadcrumbs from "@components/Breadcrumbs";
import { ImgSrc } from "@config/image";
import { Container, Row, Col } from "react-grid-system";

const Notehub = () => (
  <>
    <Container className="landingGridContainer">
      <Row>
        <Col md={12} lg={8}>
          <Breadcrumbs
            links={[
              { name: "Docs", url: "/docs" },
              { name: "Notehub", url: "/notehub" },
            ]}
          />
          <h1>Notehub</h1>
          <p>
            The hub for documentation on Notehub. Learn how to set up your
            account, create projects and device fleets, manage Notecard and host
            firmware, securely route your data, and more.
          </p>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col>
          <h2>Notehub Walkthrough</h2>
          <p>
            A comprehensive walkthrough of the features and capabilities of
            Notehub.
          </p>

          <div className="gridLinks">
            <h4>In this section</h4>
            <div className="three-column">
              <TrackedLink href="/notehub/notehub-walkthrough/#what-is-notehub">
                What is Notehub?
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#create-a-notehub-account">
                Create a Notehub Account
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#create-a-new-project">
                Create a New Project
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#sending-data-to-notehub-from-notecard">
                Sending Data to Notehub from Notecard
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#routing-data-with-notehub">
                Routing Data with Notehub
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#data-notehub-appends-to-events">
                Data Notehub Appends to Events
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#sending-data-from-notehub-to-notecard">
                Sending Data from Notehub to Notecard
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#exporting-data">
                Exporting Data
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#add-collaborators-to-a-project">
                Add Collaborators to a Project
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#invite-blues-support-to-a-project">
                Invite Blues Support to a Project
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#create-a-new-fleet">
                Create a New Fleet
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#add-a-device-to-a-fleet">
                Add a Device to a Fleet
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#using-smart-fleet-rules">
                Using Smart Fleet Rules
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#organizing-devices-by-tag">
                Organizing Devices by Tag
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#manage-firmware">
                Manage Firmware
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#configuring-your-billing-account">
                Configuring Your Billing Account
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#understanding-consumption-credits">
                Understanding Consumption Credits
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#managing-connectivity-assurance">
                Managing Connectivity Assurance
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#adding-contact-information">
                Adding Contact Information
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#add-a-favorite">
                Add a Favorite
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#creating-a-device-dashboard">
                Creating a Device Dashboard
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#finding-a-productuid">
                Finding a ProductUID
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#creating-a-new-productuid">
                Creating a New ProductUID
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#reserving-a-productuid-prefix">
                Reserving a ProductUID Prefix
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#temporarily-block-device-connections">
                Temporarily Block Device Connections
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough#configuring-notehub-alerts">
                Configuring Notehub Alerts
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough#notehub-audit-logs">
                Notehub Audit Logs
              </TrackedLink>
              <TrackedLink href="/notehub/notehub-walkthrough/#running-batch-jobs">
                Running Batch Jobs
              </TrackedLink>
            </div>
          </div>
        </Col>
      </Row>

      <hr />
      <h2>Notehub Resources</h2>

      <Row>
        <Col>
          <div className="landing-page-link-list">
            <div>
              <img
                alt=""
                src={ImgSrc("/images/icons/docs/notecard-solutions.svg")}
              />
              <h4>Notehub API Reference</h4>
              <p>
                A reference for programmatic access of Notehub via the Notehub
                API.
              </p>
              <TrackedLink
                href="/api-reference/notehub-api"
                className="landingLink"
              >
                Visit API reference
              </TrackedLink>
            </div>

            <div>
              <img
                alt=""
                src={ImgSrc("/images/icons/docs/notehub-guides.svg")}
              />
              <h4>Routing Tutorial</h4>
              <p>
                A hands-on tutorial that guides you through securely forwarding
                your Notehub data to a variety of cloud providers and IoT
                platforms.
              </p>
              <TrackedLink
                href="/guides-and-tutorials/routing-data-to-cloud"
                className="landingLink"
              >
                Start routing tutorial
              </TrackedLink>
            </div>

            <div>
              <img
                alt=""
                src={ImgSrc("/images/icons/docs/firmware-libraries.svg")}
              />
              <h4>Host Firmware Updates</h4>
              <p>
                Learn how to perform remote OTA DFU operations on your connected
                host MCUs.
              </p>
              <TrackedLink
                href="/notehub/host-firmware-updates"
                className="landingLink"
              >
                View reference
              </TrackedLink>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  </>
);

export default Notehub;
